import { CoBorrowerState } from 'prequalificationV2/actions/coBorrower';
import { BorrowerState } from 'prequalificationV2/actions/borrower';
import { ApplicationState } from 'prequalificationV2/actions/application';
import {
  getStreetAddress,
  getStreetNameFromStreetAddress,
  getStreetNumberFromStreetAddress,
  removeNonDigits,
} from 'utils/stringUtils';
import { LoanInformationVariables } from 'prequalificationV2/enums/LoanInformationVariables';
import { DataState } from 'prequalificationV2/actions/data';
import { ProveInfoState } from 'prequalificationV2/actions/proveErrorInfo';
import { ContractorNameSource } from 'enums/ApplicationsVariables';

import {
  UnderwritingBorrowerDto,
  UnderwritingCoborrowerDto,
  UnderwritingGeneralInfoDto,
} from './preQualificationApi.types';

export const mapBorrowerToUnderwritingBorrowerDto = (borrower: BorrowerState): UnderwritingBorrowerDto => ({
  firstName: borrower.firstName,
  lastName: borrower.lastName,
  dateOfBirth: borrower.dateOfBirth,
  email: borrower.email?.toLowerCase(),
  streetNumber: borrower.streetNumber,
  streetName: borrower.streetName,
  city: borrower.city,
  stateOrProvince: borrower.state,
  zipOrPostalCode: borrower.zip,
  phone: borrower.phone,
  personalIdNumber: borrower.personalIdNumber,
  borrowerIsPermanentResident: borrower.permanentResident,
  borrowerProveEligibility: borrower.eligible,
  possessionCheck: borrower.possessionCheck,
  borrowerPrefilledFirstName: borrower.prefilledFirstName,
  borrowerPrefilledLastName: borrower.prefilledLastName,
  borrowerPrefilledSsn: borrower.prefilledSsn,
  borrowerPrefilledCity: borrower.prefilledCity,
  borrowerPrefilledState: borrower.prefilledState,
  borrowerPrefilledStreetAddress: borrower.prefilledStreetAddress,
  borrowerPrefilledZipOrPostalCode: borrower.prefilledZipOrPostalCode,
  borrowerPreviousAddress: borrower.previousAddress && {
    ...borrower.previousAddress,
    street_name: getStreetNameFromStreetAddress(borrower.previousAddress.street_name as string),
    street_number: getStreetNumberFromStreetAddress(borrower.previousAddress.street_name as string),
  },
  previousMoveInDate: borrower.previousMoveInDate,
  annualIncome: borrower.annualIncome,
  isHomeImprovementSameAsPrimaryAddress: !!borrower.isHomeImprovementSameAsPrimaryAddress,
  ...(borrower.isHomeImprovementSameAsPrimaryAddress === false && {
    homeImprovementAddress: {
      address: getStreetAddress(
        borrower.homeImprovementAddress.street_number as string,
        borrower.homeImprovementAddress.street_name as string,
      ),
      city: borrower.homeImprovementAddress.city || '',
      state: (borrower.homeImprovementAddress.state_or_province as string) || '',
      zip: borrower.homeImprovementAddress.zip_or_postal_code || '',
    },
  }),
});

export const mapCoBorrowerToUnderwritingCoBorrowerDto = (coBorrower: CoBorrowerState): UnderwritingCoborrowerDto => ({
  firstName: coBorrower.firstName,
  lastName: coBorrower.lastName,
  dateOfBirth: coBorrower.dateOfBirth,
  email: coBorrower.email?.toLowerCase(),
  phone: coBorrower.phone,
  personalIdNumber: coBorrower.personalIdNumber,
  coborrowerProveEligibility: coBorrower.eligible,
  annualIncome: coBorrower.annualIncome,
});

export const mapUnderwritingGeneralDto = (application: ApplicationState, data: DataState, prove: ProveInfoState) =>
  ({
    loanAmount: +removeNonDigits(application![LoanInformationVariables.LoanAmount]),
    loanPurpose: application![LoanInformationVariables.LoanPurpose],
    otherHomeImprovementType: application[LoanInformationVariables.OtherHomeImprovementType],
    swimmingPoolType: application[LoanInformationVariables.SwimmingPoolType],
    applicationType: application[LoanInformationVariables.LoanType],
    contractorName: data.contractorName,
    contractorId: data.contractorId,
    salesPerson: data.salesPerson,
    isBorrowerManualFlow: application?.IsBorrowerManualFlow,
    isCoBorrowerManualFlow: application?.IsCoBorrowerManualFlow,
    borrowerChangedInfoAfterProvePreFill: application?.BorrowerChangedInfoAfterProvePreFill,
    coBorrowerChangedInfoAfterProvePreFill: application?.CoBorrowerChangedInfoAfterProvePreFill,
    proveErrorStatuses: prove.statuses,
    proveErrorDescriptions: prove.descriptions,
    contractorAutofillUsed: data.contractorAutofillUsed,
    network: data.network,
    device: data.device,
    promoCode: data.promoCode,
    refer: data.refer,
    // Derived from the contractorAutofillUsed so the two always agree
    contractorPromoLinkUsed: data.contractorAutofillUsed === ContractorNameSource.PromoLink,
    howDidYouHearAboutUs: data.howDidYouHearAboutUs,
    howDidYouHearAboutUsComment: data.howDidYouHearAboutUsComment,
    campaignId: data.campaignId,
    adGroupId: data.adGroupId,
    utmSource: data.utmSource,
    utmMedium: data.utmMedium,
    utmCampaign: data.utmCampaign,
    utmTerm: data.utmTerm,
    utmContent: data.utmContent,
    gclid: data.gclid,
    fbclid: data.fbclid,
    liFatId: data.liFatId,
    pintrestEpik: data.pintrestEpik,
    uetMsclkId: data.uetMsclkId,
  }) as UnderwritingGeneralInfoDto;
